import type { PendingAction } from '@/stores/aiAssistant/types'

export type OnboardingFormRecord = Record<string, unknown>

export function formKeysAffectedByAiAction(
  action: PendingAction,
  formData: OnboardingFormRecord
): string[] {
  const formKeys = new Set<string>()
  const add = (k: string | undefined) => {
    if (k && Object.prototype.hasOwnProperty.call(formData, k)) {
      formKeys.add(k)
    }
  }
  switch (action.mutation) {
    case 'navigate_to_step':
    case 'focus_field':
      break
    case 'updateCompanyTheme': {
      const leaf = String(action.field).split('.').pop()
      if (leaf === 'primary') add('primaryColor')
      if (leaf === 'secondary') add('secondaryColor')
      break
    }
    case 'updateCompany': {
      const f = action.field
      if (f === 'zip') add('zipcode')
      else if (f === 'brokerage_name') add('brokerageName')
      else if (f === 'license_number') add('licenseNumber')
      else if (f === 'website') add('website')
      else if (f === 'imprint') add('linkImprint')
      else if (f === 'privacy') add('linkPrivacy')
      else if (f === 'about') add('linkAbout')
      else add(f)
      break
    }
    case 'updateCompanyContactPerson': {
      const leaf = String(action.field).split('.').pop() ?? ''
      if (leaf === 'firstname') add('contactFirstName')
      if (leaf === 'lastname') add('contactLastName')
      if (leaf === 'phone') add('contactPhone')
      if (leaf === 'email') add('contactEmail')
      break
    }
    case 'updateAmbassador': {
      const leaf = String(action.field).split('.').pop() ?? ''
      if (leaf === 'phone') add('phone')
      if (leaf === 'mobile') add('ambassadorMobile')
      if (leaf === 'salutation') add('ambassadorSalutation')
      if (leaf === 'firstname') add('ambassadorFirstName')
      if (leaf === 'lastname') add('ambassadorLastName')
      if (leaf === 'role') add('ambassadorRole')
      if (leaf === 'email') add('ambassadorEmail')
      break
    }
    case 'updateSlogan':
      add('slogan')
      break
    default:
      break
  }
  return [...formKeys]
}

/** Writes the confirmed AI value into formData even when Apollo company is still stale. */
export function applyConfirmedAiValueToForm(
  action: PendingAction,
  keys: string[],
  formData: OnboardingFormRecord,
  countryCodeToFormDisplay: (raw: string) => string
): void {
  const pv = action.proposedValue
  if (pv === undefined || pv === null) return

  const str = typeof pv === 'string' ? pv : String(pv)

  switch (action.mutation) {
    case 'updateCompany':
    case 'updateCompanyTheme':
    case 'updateSlogan':
      for (const key of keys) {
        if (key === 'country') {
          const display = countryCodeToFormDisplay(str)
          if (display) formData.country = display
        } else {
          formData[key] = str
        }
      }
      break
    case 'updateCompanyContactPerson': {
      const leaf = String(action.field).split('.').pop() ?? ''
      if (leaf === 'firstname') formData.contactFirstName = str
      else if (leaf === 'lastname') formData.contactLastName = str
      else if (leaf === 'phone') formData.contactPhone = str
      else if (leaf === 'email') formData.contactEmail = str
      else if (keys.length) {
        for (const key of keys) formData[key] = str
      }
      break
    }
    case 'updateAmbassador': {
      const leaf = String(action.field).split('.').pop() ?? ''
      if (leaf === 'phone') formData.phone = str
      else if (leaf === 'mobile') formData.ambassadorMobile = str
      else if (leaf === 'salutation') formData.ambassadorSalutation = str
      else if (leaf === 'firstname') formData.ambassadorFirstName = str
      else if (leaf === 'lastname') formData.ambassadorLastName = str
      else if (leaf === 'role') formData.ambassadorRole = str
      else if (leaf === 'email') formData.ambassadorEmail = str
      else if (keys.length) {
        for (const key of keys) formData[key] = str
      }
      break
    }
    default:
      break
  }
}
